import React, { type FC, useEffect, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from 'tendril-v2/src/Presentation/Common/Components/ui/sheet';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';

(momentDurationFormatSetup as unknown as (m: typeof moment) => void)(moment);

interface RecordingPlayerProps {
  trigger: React.JSX.Element; // Trigger element to open the modal
  triggerButtonStyles: string; // Trigger to center icons
  sdrAudio: string; // URL for SDR call recording
  agentAudio: string; // URL for Agent call recording
  sdrAudioDuration: string; // SDR recording duration in seconds
  agentAudioDuration: string; // Agent recording duration in seconds
  recordingDateSDR: Date | null; // SDR recording date
  recordingDateAgent: Date | null; // Agent recording date
}

const RecordingPlayerModalComponent: FC<RecordingPlayerProps> = ({
  trigger,
  triggerButtonStyles,
  sdrAudio,
  agentAudio,
  sdrAudioDuration,
  agentAudioDuration,
  recordingDateSDR,
  recordingDateAgent,
}: RecordingPlayerProps) => {
  // References to audio elements
  const agentRef = useRef<HTMLAudioElement>(null);
  const sdrRef = useRef<HTMLAudioElement>(null);

  // State to manage the modal visibility
  const [isOpen, setIsOpen] = useState(false);

  // Pause all audio when the modal is closed
  useEffect(() => {
    if (!isOpen) {
      agentRef?.current?.pause();
      sdrRef?.current?.pause();
    }
  }, [isOpen]);

  /**
   * Helper for formatting dates or defaulting to "N/A"
   * @param date - Date object or null
   * @param format - Desired date format
   */
  const formatDate = (date: Date | null, format: string): string => {
    return date !== null ? moment(date).format(format) : 'N/A';
  };

  /**
   * Renders audio section (Agent or SDR)
   * @param type - Either "Agent" or "SDR"
   * @param audioSrc - Audio file URL
   * @param duration - Duration of the audio
   * @param recordingDate - Recording date
   * @param audioRef - Audio element reference
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderAudioSection = (
    type: 'Agent' | 'SDR',
    audioSrc: string,
    duration: string,
    recordingDate: Date | null,
    audioRef: React.RefObject<HTMLAudioElement>,
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  ) => {
    if (audioSrc === '') {
      return <></>;
    }

    const playingAudioRef = type === 'Agent' ? sdrRef : agentRef;

    return (
      <>
        <h2 className="font-bold text-gray-500 mt-4 mb-2">{type}</h2>
        <h3 className="font-bold text-gray-500">
          Date:
          <span className="font-light text-gray-500 ml-1">{formatDate(recordingDate, 'dddd, MMMM DD, YYYY')}</span>
        </h3>
        <h3 className="font-bold text-gray-500">
          Time:
          <span className="font-light text-gray-500 ml-1">{formatDate(recordingDate, 'h:mm A')}</span>
        </h3>

        {/* Audio player */}
        <div className="mt-4">
          <audio
            autoPlay={false} // Prevent auto playback
            preload="metadata" // Preload metadata when available
            ref={audioRef} // Attach audio element to ref
            controls // Allow user controls (Play/Pause, etc.)
            className="w-full"
            style={{
              backgroundColor: '#fff',
              color: 'white',
              borderRadius: '5px',
              padding: '5px',
            }}
            onPlay={() => {
              // Allow playing recording one by one
              if (playingAudioRef.current != null && !playingAudioRef.current.paused) {
                playingAudioRef.current.pause();
              }
            }}
          >
            <source src={audioSrc} type="audio/wav" />
            Your browser does not support audio playback.
          </audio>
        </div>
      </>
    );
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
      }}
    >
      {/* Trigger for opening the modal */}
      <SheetTrigger asChild>
        <div
          className={triggerButtonStyles}
          onClick={() => {
            setIsOpen(true);
          }}
        >
          {trigger}
        </div>
      </SheetTrigger>

      {/* Modal content */}
      <SheetContent className="w-[400px] sm:w-[540px] md:w-[768px] space-y-8" forceMount>
        {/* Modal Header */}
        <SheetHeader>
          <SheetTitle>Recordings</SheetTitle>
        </SheetHeader>

        <main>
          {/* Render Agent Recording Section */}
          {renderAudioSection('Agent', agentAudio, agentAudioDuration, recordingDateAgent, agentRef)}

          {/* Render SDR Recording Section if available */}
          {renderAudioSection('SDR', sdrAudio, sdrAudioDuration, recordingDateSDR, sdrRef)}
        </main>
      </SheetContent>
    </Sheet>
  );
};

export default RecordingPlayerModalComponent;
